import Link from 'next/link';
import { cn } from '@/lib/utils';

type ButtonProps = {
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  href?: string;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
};

const variantStyles: Record<string, string> = {
  primary:
    'bg-[#9B7B3A] text-white hover:bg-[#C4A95E] rounded-full font-semibold shadow-md hover:shadow-lg',
  secondary:
    'border-2 border-[#9B7B3A] text-[#9B7B3A] hover:bg-[#9B7B3A]/10 rounded-lg font-semibold',
  ghost:
    'text-current hover:text-[#9B7B3A] hover:bg-[#9B7B3A]/5 rounded-lg font-medium',
};

const sizeStyles: Record<string, string> = {
  sm: 'px-4 py-1.5 text-sm',
  md: 'px-6 py-2.5 text-sm',
  lg: 'px-8 py-3.5 text-base',
};

export default function Button({
  variant = 'primary',
  size = 'md',
  href,
  children,
  className,
  onClick,
}: ButtonProps) {
  const classes = cn(
    'inline-flex min-h-11 max-w-full items-center justify-center text-center transition-all duration-200 cursor-pointer',
    variantStyles[variant],
    sizeStyles[size],
    className
  );

  if (href) {
    const isExternal = href.startsWith('http') || href.startsWith('//');
    return (
      <Link
        href={href}
        className={classes}
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {children}
      </Link>
    );
  }

  return (
    <button className={classes} onClick={onClick}>
      {children}
    </button>
  );
}
